import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Head from 'src/layout/head/Head';
import { getPlaybookById, getStepTypeBadgeColor, getScheduleLabel, getPlaybookDescription, getPlaybookSchedule, getStepsDetail } from './data';
import {
  ArrowLeftIcon,
  PencilIcon,
  PlayIcon,
  PauseIcon,
  ClockIcon,
  CommandLineIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  ChevronDownIcon,
  SparklesIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  StopIcon
} from '@heroicons/react/24/outline';
import moment from 'moment';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  MarkerType,
  Handle,
  Position
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';

// Integration icons mapping
const IntegrationIcon = ({ integration, className = "w-4 h-4" }) => {
  const icons = {
    felloh: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
      </svg>
    ),
    pipedrive: (
      <div className={`${className} rounded bg-green-600 flex items-center justify-center text-white font-bold text-xs`}>P</div>
    ),
    xero: (
      <div className={`${className} rounded bg-cyan-500 flex items-center justify-center text-white font-bold text-xs`}>X</div>
    ),
    email: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    slack: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M6 15a2 2 0 01-2 2 2 2 0 01-2-2 2 2 0 012-2h2v2zm1 0a2 2 0 012-2 2 2 0 012 2v5a2 2 0 01-2 2 2 2 0 01-2-2v-5zm2-8a2 2 0 01-2-2 2 2 0 012-2 2 2 0 012 2v2H9zm0 1a2 2 0 012 2 2 2 0 01-2 2H4a2 2 0 01-2-2 2 2 0 012-2h5zm8 2a2 2 0 012-2 2 2 0 012 2 2 2 0 01-2 2h-2v-2zm-1 0a2 2 0 01-2 2 2 2 0 01-2-2V5a2 2 0 012-2 2 2 0 012 2v5zm-2 8a2 2 0 012 2 2 2 0 01-2 2 2 2 0 01-2-2v-2h2zm0-1a2 2 0 01-2-2 2 2 0 012-2h5a2 2 0 012 2 2 2 0 01-2 2h-5z" />
      </svg>
    )
  };
  return icons[integration] || <div className={`${className} rounded bg-gray-400`} />;
};

// Get badge styling based on integration
const getBadgeStyle = (integration) => {
  const styles = {
    felloh: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    pipedrive: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    xero: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300',
    email: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    slack: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
  };
  return styles[integration] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
};

// Simple connector line for legacy steps list
const Connector = ({ height = 'h-6' }) => (
  <div className={`w-0.5 ${height} bg-violet-500 dark:bg-violet-400`} />
);

// Custom React Flow Node for workflow steps
const StepNode = ({ data, selected }) => {
  const { node, stepNumber, onClick } = data;
  const hasIntegration = node.integration;
  const isPaths = node.type === 'paths' || (node.type && node.type.includes('switch'));

  return (
    <div
      onClick={() => onClick && onClick(node)}
      className={`bg-white dark:bg-gray-800 border rounded-xl shadow-sm w-[220px] transition-all hover:shadow-md cursor-pointer ${selected
        ? 'border-violet-500 dark:border-violet-400 ring-2 ring-violet-100 dark:ring-violet-900/50'
        : 'border-gray-200 dark:border-gray-700'
        }`}
    >
      <Handle type="target" position={Position.Top} style={{ left: '50%' }} className="!bg-violet-500 !w-2 !h-2 !border-0" />
      <div className="p-4">
        {/* Top row - badge and actions */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            {hasIntegration && (
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${getBadgeStyle(node.integration)}`}>
                <IntegrationIcon integration={node.integration} className="w-4 h-4" />
                {node.integration.charAt(0).toUpperCase() + node.integration.slice(1)}
              </div>
            )}
            {isPaths && (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" />
                </svg>
                Paths
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <span className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <DocumentDuplicateIcon className="w-4 h-4" />
            </span>
            <span className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <PencilIcon className="w-4 h-4" />
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-900 dark:text-white">
            {stepNumber && <span className="text-gray-500 dark:text-gray-400">{stepNumber}. </span>}
            {node.name}
          </div>
          {isPaths && <ChevronDownIcon className="w-4 h-4 text-gray-400" />}
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} style={{ left: '50%' }} className="!bg-violet-500 !w-2 !h-2 !border-0" />
    </div>
  );
};

// Custom React Flow Node for branch labels (Success, Error, etc.)
const BranchLabelNode = ({ data }) => {
  const { label, type, showChevron } = data;
  const styles = {
    success: 'bg-white dark:bg-gray-800 border-2 border-green-500 text-green-600 dark:text-green-400',
    error: 'bg-white dark:bg-gray-800 border-2 border-red-400 text-red-500 dark:text-red-400',
    path: 'bg-white dark:bg-gray-800 border-2 border-violet-400 text-violet-600 dark:text-violet-400'
  };

  const handleClasses = {
    success: '!bg-green-500',
    error: '!bg-red-400',
    path: '!bg-violet-500'
  };

  // Match step node structure exactly - same width, handles at explicit 50%
  return (
    <div className="w-[220px] text-center">
      <Handle type="target" position={Position.Top} style={{ left: '50%' }} className={`!w-2 !h-2 !border-0 ${handleClasses[type] || handleClasses.path}`} />
      <div className="inline-flex items-center justify-center gap-1.5 px-4 py-1.5">
        <span className={`inline-flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium shadow-sm whitespace-nowrap ${styles[type] || styles.path}`}>
          {label}
          {showChevron && <ChevronDownIcon className="w-4 h-4" />}
        </span>
      </div>
      <Handle type="source" position={Position.Bottom} style={{ left: '50%' }} className={`!w-2 !h-2 !border-0 ${handleClasses[type] || handleClasses.path}`} />
    </div>
  );
};

// Node types for React Flow
const nodeTypes = {
  stepNode: StepNode,
  branchLabel: BranchLabelNode
};

// Node dimensions for dagre layout
const NODE_WIDTH = 220;
const NODE_HEIGHT = 80;
const LABEL_HEIGHT = 40;

// Create dagre graph and apply layout
const getLayoutedElements = (nodes, edges) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: 'TB', nodesep: 50, ranksep: 80 });

  // Add nodes to dagre
  nodes.forEach((node) => {
    const height = node.type === 'branchLabel' ? LABEL_HEIGHT : NODE_HEIGHT;
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height });
  });

  // Add edges to dagre
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate layout
  dagre.layout(dagreGraph);

  // Apply positions to nodes
  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - (node.type === 'branchLabel' ? LABEL_HEIGHT : NODE_HEIGHT) / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

// Convert tree data to React Flow nodes and edges
const convertTreeToFlow = (tree, onNodeClick, selectedNodeId) => {
  const nodes = [];
  const edges = [];
  let stepCounter = 1;

  const processNode = (node, parentId = null, branchType = null) => {
    const nodeId = node.id;

    // Add the main step node (position will be set by dagre)
    nodes.push({
      id: nodeId,
      type: 'stepNode',
      position: { x: 0, y: 0 },
      data: { node, stepNumber: stepCounter++, onClick: onNodeClick },
      selected: nodeId === selectedNodeId
    });

    // Add edge from parent to this node
    if (parentId) {
      const edgeColor = branchType === 'success' ? '#22c55e' :
        branchType === 'error' ? '#f87171' : '#8b5cf6';
      edges.push({
        id: `e-${parentId}-${nodeId}`,
        source: parentId,
        target: nodeId,
        type: 'bezier',
        style: { stroke: edgeColor, strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor }
      });
    }

    // Process branches (side by side)
    if (node.branches && node.branches.length > 0) {
      node.branches.forEach((branch) => {
        const labelId = `label-${branch.id}`;

        // Add branch label node
        nodes.push({
          id: labelId,
          type: 'branchLabel',
          position: { x: 0, y: 0 },
          data: { label: branch.label, type: branch.type, showChevron: branch.type === 'path' }
        });

        // Edge from parent to label
        const branchColor = branch.type === 'success' ? '#22c55e' :
          branch.type === 'error' ? '#f87171' : '#8b5cf6';
        edges.push({
          id: `e-${nodeId}-${labelId}`,
          source: nodeId,
          target: labelId,
          type: 'bezier',
          style: { stroke: branchColor, strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: branchColor }
        });

        // Process branch children
        if (branch.children) {
          branch.children.forEach((child) => {
            processNode(child, labelId, branch.type);
          });
        }
      });
    }

    // Process linear children
    if (node.children && node.children.length > 0 && (!node.branches || node.branches.length === 0)) {
      node.children.forEach((child) => {
        processNode(child, nodeId, branchType);
      });
    }
  };

  processNode(tree, null, null);

  // Apply dagre layout
  return getLayoutedElements(nodes, edges);
};

// Inner component that uses React Flow hooks
const WorkflowTreeInner = ({ nodes, edges, onNodeClick }) => {
  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodeClick={onNodeClick}
      nodeTypes={nodeTypes}
      defaultEdgeOptions={{
        type: 'bezier',
        style: { strokeWidth: 2 }
      }}
      fitView
      fitViewOptions={{ padding: 0.2, includeHiddenNodes: false }}
      minZoom={0.2}
      maxZoom={1.5}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      panOnDrag={true}
      zoomOnScroll={true}
      proOptions={{ hideAttribution: true }}
    >
      <Background color="#e5e7eb" gap={16} size={1} />
    </ReactFlow>
  );
};

// Main workflow tree component using React Flow
const WorkflowTree = ({ tree, onNodeSelect, selectedNodeId }) => {
  const { nodes, edges } = useMemo(
    () => tree ? convertTreeToFlow(tree, onNodeSelect, selectedNodeId) : { nodes: [], edges: [] },
    [tree, onNodeSelect, selectedNodeId]
  );

  const onNodeClick = useCallback((_event, node) => {
    if (node.data.onClick && node.data.node) {
      node.data.onClick(node.data.node);
    }
  }, []);

  if (!tree) return null;

  return (
    <div className="h-[600px] w-full rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
      <style>{`
        .react-flow__edges {
          width: 100% !important;
          height: 100% !important;
        }
      `}</style>
      <ReactFlowProvider>
        <WorkflowTreeInner nodes={nodes} edges={edges} onNodeClick={onNodeClick} />
      </ReactFlowProvider>
    </div>
  );
};

// Step detail panel shown when a tree node is clicked
const StepDetailPanel = ({ node, onClose }) => {
  if (!node) return null;

  const hasIntegration = node.integration;
  const configEntries = node.config
    ? Object.entries(node.config).filter(([key]) => key !== 'integration')
    : [];

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 min-w-[320px] max-w-[400px]">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {hasIntegration && (
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${getStepTypeBadgeColor(null, node.integration)}`}>
                <IntegrationIcon integration={node.integration} className="w-3.5 h-3.5" />
                {node.integration.charAt(0).toUpperCase() + node.integration.slice(1)}
              </span>
            )}
            {node.type && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                {node.type}
              </span>
            )}
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {node.name}
          </h3>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
        >
          <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        </button>
      </div>

      {node.description && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {node.description}
        </p>
      )}

      {configEntries.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            Configuration
          </h4>
          <div className="bg-gray-50 dark:bg-gray-900 rounded-md p-3 font-mono text-xs space-y-1.5">
            {configEntries.map(([key, value]) => (
              <div key={key} className="break-all">
                <span className="text-gray-500 dark:text-gray-400">{key}:</span>{' '}
                <span className="text-green-600 dark:text-green-400">
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {node.branches && node.branches.length > 0 && (
        <div className="mt-4">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            Branches
          </h4>
          <div className="space-y-1">
            {node.branches.map((branch) => (
              <div
                key={branch.id}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium mr-2 ${branch.type === 'success'
                  ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                  : branch.type === 'error'
                    ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300'
                    : 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300'
                  }`}
              >
                {branch.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Legacy flat steps renderer for playbooks without tree structure
const LegacyStepsList = ({ steps }) => {
  return (
    <div className="space-y-4">
      {steps.map((step, index) => (
        <div key={step.id} className="flex flex-col items-center">
          {index > 0 && <Connector height="h-6" />}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4 max-w-md w-full">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center">
                <span className="text-xs font-semibold text-violet-700 dark:text-violet-300">{index + 1}</span>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{step.name}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStepTypeBadgeColor(step.type)}`}>
                    {step.type}
                  </span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400">{step.description}</p>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Step type icon for the steps view
const StepTypeIcon = ({ type }) => {
  const icons = {
    webhook: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    ),
    condition: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" />
      </svg>
    ),
    action: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
    ),
    delay: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    ),
    schedule: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
    loop: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 1l4 4-4 4" />
        <path d="M3 11V9a4 4 0 0 1 4-4h14" />
        <path d="M7 23l-4-4 4-4" />
        <path d="M21 13v2a4 4 0 0 1-4 4H3" />
      </svg>
    )
  };
  return icons[type] || icons.action;
};

// Format config value for display
const formatConfigValue = (value) => {
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value);
  }
  return String(value);
};

// Steps view - detailed list format matching dashboard style
const StepsView = ({ steps }) => {
  return (
    <div>
      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-6">
        Steps
      </div>
      <div className="space-y-3">
        {steps.map((step, index) => {
          const hasIntegration = step.config?.integration;
          const configEntries = step.config
            ? Object.entries(step.config).filter(([key]) => key !== 'integration')
            : [];

          return (
            <div key={step.id} className="rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              {/* Header row */}
              <div className="flex items-start gap-4">
                {/* Step number */}
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">{index + 1}</span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {/* Title row with badges */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {step.name}
                    </span>
                    {/* Type badge */}
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-600 dark:text-gray-300">
                      <StepTypeIcon type={step.type} />
                      {step.type}
                    </span>
                    {/* Integration badge */}
                    {hasIntegration && (
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStepTypeBadgeColor(null, step.config.integration)}`}>
                        <IntegrationIcon integration={step.config.integration} className="w-3.5 h-3.5" />
                        {step.config.integration}
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {step.description}
                  </p>
                </div>
              </div>

              {/* Config display */}
              {configEntries.length > 0 && (
                <div className="mt-3 ml-13 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-md p-3 font-mono text-xs space-y-1">
                    {configEntries.map(([key, value]) => (
                      <div key={key} className="break-all">
                        <span className="text-gray-500 dark:text-gray-400">{key}:</span>{' '}
                        <span className="text-green-600 dark:text-green-400">{formatConfigValue(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Frequency options for edit modal
const FREQUENCY_OPTIONS = [
  { value: 'on_event', label: 'On event trigger' },
  { value: 'every_5_minutes', label: 'Every 5 minutes' },
  { value: 'every_15_minutes', label: 'Every 15 minutes' },
  { value: 'every_hour', label: 'Every hour' },
  { value: 'every_6_hours', label: 'Every 6 hours' },
  { value: 'every_day', label: 'Every day' },
  { value: 'every_week', label: 'Every week' },
  { value: 'every_month', label: 'Every month' },
];

// Format duration in human readable format
const formatDuration = (ms) => {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

// Test Run Status Box component
const TestRunStatusBox = ({ testRun, onClose, onStop, stepsCount }) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const intervalRef = useRef(null);

  const status = testRun?.status;
  const startTime = testRun?.startTime;
  const currentStep = testRun?.currentStep;
  const completedSteps = testRun?.completedSteps || 0;
  const error = testRun?.error;

  useEffect(() => {
    if (!testRun) return;

    if (status === 'running') {
      intervalRef.current = setInterval(() => {
        setElapsedTime(Date.now() - startTime);
      }, 100);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setElapsedTime(testRun.duration || Date.now() - startTime);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [testRun, status, startTime]);

  if (!testRun) return null;

  const progressPercent = stepsCount > 0 ? Math.round((completedSteps / stepsCount) * 100) : 0;

  const statusConfig = {
    running: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      icon: <ArrowPathIcon className="w-5 h-5 text-blue-500 animate-spin" />,
      text: 'text-blue-700 dark:text-blue-300',
      label: 'Running'
    },
    success: {
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      icon: <CheckCircleIcon className="w-5 h-5 text-green-500" />,
      text: 'text-green-700 dark:text-green-300',
      label: 'Completed'
    },
    error: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      icon: <ExclamationCircleIcon className="w-5 h-5 text-red-500" />,
      text: 'text-red-700 dark:text-red-300',
      label: 'Failed'
    },
    stopped: {
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      border: 'border-yellow-200 dark:border-yellow-800',
      icon: <StopIcon className="w-5 h-5 text-yellow-500" />,
      text: 'text-yellow-700 dark:text-yellow-300',
      label: 'Stopped'
    }
  };

  const config = statusConfig[status] || statusConfig.running;

  return (
    <div className={`mb-6 rounded-lg border ${config.border} ${config.bg} p-4 overflow-hidden`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="flex-shrink-0">
            {config.icon}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className={`text-sm font-semibold ${config.text}`}>
                Test Run {config.label}
              </h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Started {moment(startTime).format('HH:mm:ss')}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">•</span>
              <span className="text-xs text-gray-600 dark:text-gray-400 font-mono">{formatDuration(elapsedTime)}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">•</span>
              <span className="text-xs text-gray-600 dark:text-gray-400">{completedSteps}/{stepsCount} steps</span>
              {currentStep && status === 'running' && (
                <>
                  <span className="text-xs text-gray-500 dark:text-gray-400">•</span>
                  <span className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-[150px]">{currentStep}</span>
                </>
              )}
            </div>

            {error && (
              <div className="mt-2 text-sm text-red-600 dark:text-red-400">
                <span className="font-medium">Error:</span> {error}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Progress bar */}
          <div className="w-32 hidden sm:block">
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${status === 'running' ? 'bg-blue-500' : status === 'success' ? 'bg-green-500' : status === 'error' ? 'bg-red-500' : 'bg-yellow-500'}`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400 w-8 text-right">{progressPercent}%</span>

          {status === 'running' && (
            <button
              onClick={onStop}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/50 hover:bg-red-200 dark:hover:bg-red-900 rounded-md transition-colors"
            >
              <StopIcon className="w-4 h-4" />
              Stop
            </button>
          )}
          {status !== 'running' && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// View mode tabs
const ViewTabs = ({ activeView, onViewChange, versionsCount = 0, logsCount = 0 }) => {
  const baseClasses = 'px-4 py-2 text-sm font-medium rounded-md transition-colors';
  const activeClasses = 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm';
  const inactiveClasses = 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white';

  const tabs = [
    { id: 'tree', label: 'Tree View' },
    { id: 'steps', label: 'Steps View' },
    { id: 'versions', label: 'Versioning', count: versionsCount },
    { id: 'logs', label: 'Run Logs', count: logsCount },
  ];

  return (
    <div className="inline-flex items-stretch p-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
      {tabs.map((tab, index) => (
        <div key={tab.id} className="flex items-center">
          {index > 0 && (
            <div className="w-px h-5 bg-gray-300 dark:bg-gray-500 mx-0.5" />
          )}
          <button
            onClick={() => onViewChange(tab.id)}
            className={`${baseClasses} ${activeView === tab.id ? activeClasses : inactiveClasses}`}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className={`ml-1.5 px-1.5 py-0.5 text-xs rounded-full ${activeView === tab.id
                ? 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300'
                : 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                }`}>
                {tab.count}
              </span>
            )}
          </button>
        </div>
      ))}
    </div>
  );
};

const PlaybookDetail = () => {
  const { id } = useParams();
  const playbook = getPlaybookById(id);
  const [viewMode, setViewMode] = useState('tree');
  const [selectedNode, setSelectedNode] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    frequency: 'on_event',
    changes: ''
  });
  const [testRun, setTestRun] = useState(null);
  const testRunTimerRef = useRef(null);
  const [isEnabled, setIsEnabled] = useState(playbook?.meta?.enabled ?? playbook?.active ?? true);

  const handleNodeSelect = (node) => {
    setSelectedNode(selectedNode?.id === node.id ? null : node);
  };

  const toggleEditModal = () => {
    if (!editModalOpen && playbook) {
      // Initialize form with current playbook data
      const schedule = getPlaybookSchedule(playbook);
      setEditFormData({
        name: playbook.name,
        frequency: schedule === 'on_event' ? 'on_event' : schedule,
        changes: ''
      });
    }
    setEditModalOpen(!editModalOpen);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    // TODO: Implement playbook update API call
    console.log('Updating playbook:', editFormData);
    toggleEditModal();
  };

  // Test run handlers
  const startTestRun = useCallback(() => {
    const steps = playbook?.nodes || [];
    const stepNames = steps.map(s => s.name || s.type);

    setTestRun({
      status: 'running',
      startTime: Date.now(),
      currentStep: stepNames[0] || 'Initializing',
      completedSteps: 0,
      error: null
    });

    // Simulate step execution
    let currentStepIndex = 0;
    const totalSteps = steps.length || 3;

    const simulateSteps = () => {
      testRunTimerRef.current = setTimeout(() => {
        currentStepIndex++;

        if (currentStepIndex >= totalSteps) {
          // Randomly succeed or fail for demo
          const success = Math.random() > 0.2;
          setTestRun(prev => ({
            ...prev,
            status: success ? 'success' : 'error',
            completedSteps: totalSteps,
            currentStep: null,
            duration: Date.now() - prev.startTime,
            error: success ? null : 'Failed to execute step: API timeout'
          }));
        } else {
          setTestRun(prev => ({
            ...prev,
            completedSteps: currentStepIndex,
            currentStep: stepNames[currentStepIndex] || `Step ${currentStepIndex + 1}`
          }));
          simulateSteps();
        }
      }, 800 + Math.random() * 1200);
    };

    simulateSteps();
  }, [playbook]);

  const stopTestRun = useCallback(() => {
    if (testRunTimerRef.current) {
      clearTimeout(testRunTimerRef.current);
    }
    setTestRun(prev => prev ? {
      ...prev,
      status: 'stopped',
      duration: Date.now() - prev.startTime
    } : null);
  }, []);

  const closeTestRun = useCallback(() => {
    setTestRun(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (testRunTimerRef.current) {
        clearTimeout(testRunTimerRef.current);
      }
    };
  }, []);

  if (!playbook) {
    return (
      <div className="text-center py-12">
        <CommandLineIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Playbook not found</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          The playbook you&apos;re looking for doesn&apos;t exist.
        </p>
        <div className="mt-6">
          <Link
            to="/playbooks"
            className="inline-flex items-center gap-2 px-4 py-2 bg-violet-800 hover:bg-violet-600 text-white rounded-md text-sm font-medium transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Playbooks
          </Link>
        </div>
      </div>
    );
  }

  // Derive data from n8n format
  const description = getPlaybookDescription(playbook);
  const schedule = getPlaybookSchedule(playbook);
  const stepsDetail = getStepsDetail(playbook);
  const stepsCount = playbook.nodes?.length || 0;
  const runs = playbook.meta?.runs || 0;
  const lastRun = playbook.meta?.lastRun;

  const toggleEnabled = () => {
    setIsEnabled(prev => !prev);
    // TODO: Implement API call to update playbook status
  };

  return (
    <React.Fragment>
      <Head title={playbook ? playbook.name : 'Playbook'} />
    <div className="pb-12">
      {/* Breadcrumb */}
      <div className="mb-6">
        <Link
          to="/playbooks"
          className="inline-flex items-center gap-2 text-sm text-violet-800 hover:text-violet-600 dark:text-violet-400 dark:hover:text-violet-300 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Playbooks
        </Link>
      </div>

      {/* Test Run Status Box */}
      <TestRunStatusBox
        testRun={testRun}
        onClose={closeTestRun}
        onStop={stopTestRun}
        stepsCount={playbook.nodes?.length || 0}
      />

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6 clearfix h-auto">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {playbook.name}
              </h1>
              {isEnabled ? (
                <span className="inline-flex clearfix items-center gap-1 px-3 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                  <PlayIcon className="w-4 h-4" />
                  Enabled
                </span>
              ) : (
                <span className="inline-flex clearfix items-center gap-1 px-3 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300">
                  <PauseIcon className="w-4 h-4" />
                  Disabled
                </span>
              )}
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {playbook.versionId}
              </span>
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              {description}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleEnabled}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${isEnabled
                ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-300 dark:hover:bg-yellow-800'
                : 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-300 dark:hover:bg-green-800'
                }`}
            >
              {isEnabled ? (
                <>
                  <PauseIcon className="w-4 h-4" />
                  Disable
                </>
              ) : (
                <>
                  <PlayIcon className="w-4 h-4" />
                  Enable
                </>
              )}
            </button>
            <button
              onClick={startTestRun}
              disabled={testRun?.status === 'running'}
              className={`inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-md text-sm font-medium transition-colors ${
                testRun?.status === 'running'
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
            >
              <PlayIcon className="w-4 h-4" />
              Test Run
            </button>
            <button
              onClick={toggleEditModal}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <PencilIcon className="w-4 h-4" />
              Edit
            </button>
          </div>
        </div>

        <div className="clearfix h-auto relative pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 dark:border-gray-700 clearfix h-auto">
            <div className="flex items-center gap-3 clearfix h-auto">
              <CommandLineIcon className="w-5 h-5 text-gray-400" />
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Steps</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">{stepsCount}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 clearfix h-auto">
              <ClockIcon className="w-5 h-5 text-gray-400" />
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Schedule</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">{getScheduleLabel(schedule)}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 clearfix h-auto">
              <ClockIcon className="w-5 h-5 text-gray-400" />
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Runs</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">{runs}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 clearfix h-auto">
              <ClockIcon className="w-5 h-5 text-gray-400" />
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Last Run</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {lastRun ? moment(lastRun).format('MMM D, YYYY [at] HH:mm') : 'Never'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 clearfix h-auto mb-4">
        <ViewTabs
          activeView={viewMode}
          onViewChange={setViewMode}
          versionsCount={(playbook.versions || []).length}
          logsCount={(playbook.logs || []).length}
        />
      </div>

      {/* Workflow Section */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        {viewMode === 'tree' && (
          <div className="flex items-center justify-between mb-6 flex-wrap">
            {/* Legend - only show for tree view */}

            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border-2 border-green-500 bg-white" />
                <span className="text-gray-600 dark:text-gray-400">Success</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border-2 border-red-400 bg-white" />
                <span className="text-gray-600 dark:text-gray-400">Error</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border-2 border-violet-400 bg-white" />
                <span className="text-gray-600 dark:text-gray-400">Path</span>
              </div>
            </div>

          </div>
        )}

        <div className="flex gap-6">
          <div className="flex-1 overflow-x-auto pb-4">
            {viewMode === 'tree' && (
              playbook.workflowTree ? (
                <WorkflowTree
                  tree={playbook.workflowTree}
                  onNodeSelect={handleNodeSelect}
                  selectedNodeId={selectedNode?.id}
                />
              ) : (
                <LegacyStepsList steps={stepsDetail} />
              )
            )}

            {viewMode === 'steps' && (
              <StepsView steps={stepsDetail} />
            )}

            {viewMode === 'versions' && (
              <div className="space-y-3">
                {[...(playbook.versions || [])]
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .map((version) => (
                    <div
                      key={version.id}
                      className={`p-4 rounded-lg border ${version.isCurrent
                        ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                        : 'border-gray-200 dark:border-gray-700'
                        }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-base font-semibold text-gray-900 dark:text-white">
                            v{version.version}
                          </span>
                          {version.isCurrent && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-300">
                              Current
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {moment(version.createdAt).format('MMM D, YYYY [at] HH:mm')}
                          </span>
                          {!version.isCurrent && (
                            <button
                              onClick={() => {
                                // TODO: Implement revert API call
                                console.log('Reverting to version:', version.version);
                              }}
                              className="inline-flex h-auto items-center gap-1 px-3 py-1 text-sm font-medium text-violet-700 dark:text-violet-300 bg-violet-100 dark:bg-violet-900/50 hover:bg-violet-200 dark:hover:bg-violet-900 rounded-md transition-colors"
                            >
                              <ArrowPathIcon className="w-4 h-4" />
                              Revert
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                        {version.changeDescription}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">
                        by {version.createdBy}
                      </p>
                    </div>
                  ))}
              </div>
            )}

            {viewMode === 'logs' && (
              <div className="space-y-2">
                {[...(playbook.logs || [])]
                  .sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt))
                  .map((log) => (
                    <div
                      key={log.id}
                      className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          {log.status === 'success' && (
                            <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                          )}
                          {log.status === 'error' && (
                            <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                          )}
                          {log.status === 'warning' && (
                            <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                          )}
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {log.triggerData?.booking_reference || log.executionId}
                          </span>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${log.status === 'success'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                            : log.status === 'error'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                            }`}>
                            {log.status}
                          </span>
                        </div>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {moment(log.startedAt).format('MMM D, YYYY [at] HH:mm:ss')}
                        </span>
                      </div>
                      <div className="flex items-center gap-6 text-sm text-gray-500 dark:text-gray-400">
                        <span>{log.stepsExecuted} steps executed</span>
                        <span>{log.duration}ms duration</span>
                        <span>v{log.version}</span>
                        {log.triggerData?.amount && (
                          <span>£{log.triggerData.amount.toLocaleString()}</span>
                        )}
                      </div>
                      {log.error && (
                        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-600 dark:text-red-400">
                          <span className="font-medium">{log.error.step}:</span> {log.error.message}
                        </div>
                      )}
                      {log.warning && (
                        <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-sm text-yellow-600 dark:text-yellow-400">
                          <span className="font-medium">{log.warning.step}:</span> {log.warning.message}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Detail panel */}
          {viewMode === 'tree' && selectedNode && (
            <div className="flex-shrink-0">
              <StepDetailPanel
                node={selectedNode}
                onClose={() => setSelectedNode(null)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Edit Playbook Modal */}
      <Modal isOpen={editModalOpen} toggle={toggleEditModal} size="lg">
        <ModalHeader
          toggle={toggleEditModal}
          className="bg-violet-800 text-xl text-white pt-4 pb-3"
          close={
            <button className="-mt-1 text-white" onClick={toggleEditModal}>
              <XMarkIcon className="h-6 w-6" />
            </button>
          }
        >
          <span className="flex items-center gap-2">
            <PencilIcon className="h-5 w-5" />
            Edit Playbook
          </span>
        </ModalHeader>

        <form onSubmit={handleEditSubmit}>
          <ModalBody className="p-6">
            <div className="space-y-6">
              {/* Name Field */}
              <div>
                <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Playbook Name
                </label>
                <input
                  type="text"
                  id="edit-name"
                  name="name"
                  value={editFormData.name}
                  onChange={handleEditInputChange}
                  placeholder="e.g., Payment Confirmation Flow"
                  className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                  required
                />
              </div>

              {/* Frequency Field */}
              <div>
                <label htmlFor="edit-frequency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  How often should this run?
                </label>
                <select
                  id="edit-frequency"
                  name="frequency"
                  value={editFormData.frequency}
                  onChange={handleEditInputChange}
                  className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                >
                  {FREQUENCY_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Changes Description Field - LLM Style */}
              <div>
                <label htmlFor="edit-changes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  What changes would you like to make?
                </label>
                <div className="relative">
                  <textarea
                    id="edit-changes"
                    name="changes"
                    value={editFormData.changes}
                    onChange={handleEditInputChange}
                    placeholder="Describe the changes you want to make in natural language. For example: 'Add an additional check to verify the customer email before sending the confirmation. Also add a Slack notification when a payment fails.'"
                    rows={6}
                    className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 resize-none"
                  />
                  <div className="absolute bottom-3 right-3 flex items-center gap-1 text-xs text-gray-400">
                    <SparklesIcon className="h-4 w-4" />
                    AI-powered
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Our AI will analyse your changes and update the playbook steps automatically.
                </p>
              </div>
            </div>
          </ModalBody>

          <ModalFooter className="bg-light">
            <button
              type="button"
              onClick={toggleEditModal}
              className="btn btn-outline-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn bg-violet-700 hover:bg-violet-800 text-white"
            >
              <SparklesIcon className="w-4 h-4 inline mr-1 -mt-0.5" />
              Update Playbook
            </button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
    </React.Fragment>
  );
};

export default PlaybookDetail;
